<template>
    <div>
        <DCard
            :title="__('contracts.pricing.messages.upload_new_pricing')"
            padding
        >
            <DFormRow
                :label="__('contracts.pricing.form.name')"
                is-required
            >
                <DInput
                    id="pricing-upload-name"
                    v-model="form.name"
                    requried
                    :invalid-feedback="errors.name"
                />
            </DFormRow>
            <div class="file-section">
                <DFormRow
                    :label="__('contracts.pricing.form.file')"
                    is-required
                    class="pricing-upload-file"
                >
                    <DFileInput
                        id="pricing-upload-file"
                        :custom="true"
                        :multiple="false"
                        :invalid-feedback="errors.file"
                        :file-with-errors="fileWithErrors"
                        @change="addFile"
                        @reset-file-with-errors="resetFileWithErrors"
                    />
                    <p v-if="form.file">
                        {{ form.file.name }}
                    </p>
                </DFormRow>
                <DButton
                    icon="download"
                    :label="__('contracts.pricing.form.download_csv')"
                    @click="downloadCsv"
                />
            </div>
            <DFormRow
                :label="__('contracts.pricings.price_communication')"
                is-required
            >
                <DSelect
                    id="pricing-upload-price-communication"
                    v-model="form.price_communication"
                    :options="props.priceCommunication"
                />
            </DFormRow>
            <DFormRow
                :label="__('contracts.pricing.form.inform_customers')"
                is-required
            >
                <DFormCheck
                    id="pricing-upload-inform-customers"
                    v-model="form.inform_customers"
                />
            </DFormRow>
            <DFormRow
                :label="__('contracts.pricing.form.single_email_per_customer')"
                is-required
            >
                <DFormCheck
                    id="pricing-upload-single-email-per-customer"
                    v-model="form.single_email_per_customer"
                />
            </DFormRow>
            <DFormRow
                :label="__('contracts.pricings.mail_template')"
                is-required
            >
                <DSelect
                    id="pricing-upload-mail-template"
                    v-model="form.mail_template"
                    :options="props.mailTemplates"
                />
            </DFormRow>
            <DFormRow
                :label="__('contracts.pricing.form.domain_decoupling_discounts')"
                :hint="__('contracts.pricing.form.discount_description')"
                is-required
            >
                <DFormCheck
                    id="pricing-domain-decoupling-discounts"
                    v-model="form.enable_domain_discounts"
                />
            </DFormRow>

            <!-- Default domain decoupling discount -->
            <DFormRow
                v-if="form.enable_domain_discounts"
                :label="__('contracts.pricing.form.domain_decoupling_default_label')"
                is-required
            >
                <div class="form-fields">
                    <!-- Discount name -->
                    <div class="form-field mb-3">
                        <DInput
                            v-model="form.domain_decoupling_default_discount_name"
                            :placeholder="__('contracts.pricing.form.discount_name')"
                            :is-valid="!errors[`domain_decoupling_default_discount_name`]"
                        />
                        <p
                            v-if="errors[`domain_decoupling_default_discount_name`]"
                            class="error-msg"
                        >
                            {{ errors[`domain_decoupling_default_discount_name`] }}
                        </p>
                    </div>

                    <!-- Discount value -->
                    <div class="form-field mb-3">
                        <DInput
                            v-model="form.domain_decoupling_default_discount_value"
                            type="number"
                            :placeholder="__('contracts.pricing.form.discount_value')"
                            :is-valid="!errors[`domain_decoupling_default_discount_value`]"
                        />
                        <p
                            v-if="errors[`domain_decoupling_default_discount_value`]"
                            class="error-msg"
                        >
                            {{ errors[`domain_decoupling_default_discount_value`] }}
                        </p>
                    </div>

                    <!-- Discount type -->
                    <div class="form-field mb-3">
                        <DSelect
                            id="domain_decoupling_default_discount_type"
                            value="percentage"
                            :options="[
                                {
                                    value: 'percentage',
                                    label: __('contracts.pricing.form.discount_percentage')
                                }
                            ]"
                            :disabled="true"
                        />
                    </div>
                </div>
            </DFormRow>

            <!-- Domain Discounts Section -->
            <DomainDiscountsForm
                v-if="form.enable_domain_discounts"
                ref="domainDiscountsRef"
                v-model="form.domain_decoupling_discounts"
                :tld-options="tldOptions"
                :errors="errors"
            />
        </DCard>
        <DCard
            v-if="errors.errorMessage"
            :title="__('contracts.pricing.messages.file_errors')"
            padding
            class="mt-3"
        >
            <DRow
                v-for="(error, index) in errors"
                :key="index"
            >
                <DFormRow
                    v-if="error.length > 0 && index !== 'errorMessage'"
                    :label="__(`contracts.pricing.messages.${index}`)"
                >
                    {{ error }}
                </DFormRow>
            </DRow>
        </DCard>
    </div>
    <DFormControls
        :dirty="form.isDirty"
        :submit-message="__('contracts.pricing.messages.preview')"
        submit-icon="eye"
        :cancel-route="cancelRoute"
        @submit="submitForm"
    />
</template>

<script setup>
import { route } from '~@/Composables/route'
import { useForm } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { computed, ref, watch } from 'vue'
import DomainDiscountsForm from '~@/Components/Contracts/Pricing/DomainDiscountsForm.vue'

const props = defineProps({
    errors: {
        type: Object,
        default: () => {
            return {}
        }
    },
    priceCommunication: {
        type: Array,
        default: () => {
            return []
        }
    },
    mailTemplates: {
        type: Array,
        default: () => {
            return []
        }
    },
    tldOptions: {
        type: Array,
        default: () => []
    },
})

const form = useForm({
    name: null,
    file: null,
    inform_customers: true,
    single_email_per_customer: true,
    price_communication: null,
    mail_template: 'default_pricing',
    enable_domain_discounts: false,
    domain_decoupling_discounts: [],
    domain_decoupling_default_discount_name: null,
    domain_decoupling_default_discount_value: null
})

const downloadCsv = () => {
    const link = document.createElement('a')
    link.href = route('contracts.pricing.download-template')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const fileWithErrors = ref(false)

function addFile (fileList) {
    form.file = fileList[0]
}

function submitForm () {
    form.post(route('contracts.pricing.upload'), {
        onError: () => {
            form.file = null
            fileWithErrors.value = true
        }
    })
}

function resetFileWithErrors () {
    fileWithErrors.value = false
}

const cancelRoute = computed(() => {
    return route('contracts.pricing.index')
})

// To remove array data if domain discounts are disabled, to not show errors in validation
watch(() => form.enable_domain_discounts, (enabled) => {
    if (!enabled) {
        form.domain_decoupling_discounts = []
    }
})

</script>

<style scoped lang="scss">
    .pricing-upload-file {
        margin-bottom: 5px!important;
    }
    .file-section {
        margin-bottom: 25px;
    }
    .form-fields {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
    }
    .form-field {
        flex: 1 1 30%;
        min-width: 180px;
    }
</style>
